import React, { useEffect } from 'react';
import { XCircle, CheckCircle, AlertCircle } from 'lucide-react';

const ExamReview = ({ attempt, onBack }) => {
  useEffect(() => {
    const disable = (e) => e.preventDefault();
    document.addEventListener('copy', disable);
    document.addEventListener('cut', disable);
    document.addEventListener('contextmenu', disable);
    document.addEventListener('selectstart', disable);

    const style = document.createElement('style');
    style.innerHTML = `
      *::selection { background: transparent !important; }
      body, .nocopy, .nocopy * {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
      }
      pre, code {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.removeEventListener('copy', disable);
      document.removeEventListener('cut', disable);
      document.removeEventListener('contextmenu', disable);
      document.removeEventListener('selectstart', disable);
      document.head.removeChild(style);
    };
  }, []);

  if (!attempt || !attempt.questions) return <p>No attempt data available.</p>;

  const { questions, answers, score, percentage } = attempt;

  const alphabet = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I'];

  // ✅ Separate unanswered from incorrect
  const unanswered = questions.filter((q) => {
    const qid = String(q.questionId);
    const userAns = answers[qid];
    return !userAns || userAns.length === 0;
  });

  // ✅ Only show questions that were answered but incorrect
  const incorrect = questions.filter((q) => {
    const qid = String(q.questionId);
    const userAns = answers[qid];
    
    // Skip unanswered - don't show these with answers
    if (!userAns || userAns.length === 0) return false;

    const correct = Array.isArray(q.correctAnswers)
      ? q.correctAnswers.map((a) => a.toString().trim().toUpperCase())
      : (q.correctAnswer
          ? q.correctAnswer.toString().split(',').map((a) => a.trim().toUpperCase())
          : []);

    const userLetters = userAns
      .map((ans) => {
        const idx = (q.options || []).findIndex(
          (o) => o.trim() === ans.trim()
        );
        return idx >= 0 ? alphabet[idx] : null;
      })
      .filter(Boolean);

    const isCorrect =
      correct.length === userLetters.length &&
      correct.every((c) => userLetters.includes(c));

    return !isCorrect;
  });

  const totalFailed = unanswered.length + incorrect.length;

  const cardStyle = {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 24,
    marginBottom: 20,
    boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
  };

  const headingStyle = {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 20,
    textAlign: 'center',
    color: '#1e293b',
  };

  return (
    <div
      className="nocopy"
      onContextMenu={(e) => e.preventDefault()}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
        padding: 40,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {/* ✅ MOBILE RESPONSIVE CSS */}
      <style>{`
        @media (max-width: 768px) {
          .review-container {
            padding: 16px !important;
          }
          .review-card {
            padding: 16px !important;
          }
          .review-heading {
            font-size: 18px !important;
          }
        }
      `}</style>

      <div className="review-container" style={{ maxWidth: 1000, margin: '0 auto' }}>
        <div className="review-card" style={cardStyle}>
          <h2 className="review-heading" style={headingStyle}>
            {totalFailed === 0 ? (
              <>
                <CheckCircle
                  size={32}
                  color="#10b981"
                  style={{ verticalAlign: 'middle', marginRight: 8 }}
                />
                All Answers Correct 🎉
              </>
            ) : (
              <>
                <XCircle
                  size={32}
                  color="#ef4444"
                  style={{ verticalAlign: 'middle', marginRight: 8 }}
                />
                Review Mistakes ({totalFailed})
              </>
            )}
          </h2>

          <div style={{ marginBottom: 20, textAlign: 'center' }}>
            <p style={{ color: '#64748b', fontSize: 14 }}>
              Score: <strong>{score}</strong> | Percentage:{' '}
              <strong>{percentage}%</strong> | Total Questions:{' '}
              {questions.length}
            </p>
          </div>

          {totalFailed === 0 ? (
            <p
              style={{
                color: '#10b981',
                backgroundColor: '#ecfdf5',
                border: '1px solid #bbf7d0',
                borderRadius: 12,
                padding: 16,
                textAlign: 'center',
              }}
            >
              Excellent work — you answered every question correctly!
            </p>
          ) : (
            <>
              {/* ✅ Show unanswered summary WITHOUT revealing answers */}
              {unanswered.length > 0 && (
                <div
                  style={{
                    backgroundColor: '#fffbeb',
                    border: '1px solid #fcd34d',
                    borderRadius: 12,
                    padding: 20,
                    marginBottom: 24,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 12,
                      color: '#92400e',
                      fontWeight: 600,
                      fontSize: 16,
                    }}
                  >
                    <AlertCircle size={20} style={{ marginRight: 8 }} />
                    Unanswered Questions: {unanswered.length}
                  </div>
                  <p
                    style={{
                      color: '#78350f',
                      fontSize: 14,
                      margin: 0,
                      lineHeight: 1.6,
                    }}
                  >
                    You did not answer {unanswered.length} question{unanswered.length !== 1 ? 's' : ''}.
                    These are counted as incorrect. Make sure to attempt all questions in future exams.
                  </p>
                </div>
              )}

              {/* ✅ Show only answered but incorrect questions with explanations */}
              {incorrect.length > 0 && (
                <>
                  <h3
                    style={{
                      fontSize: 18,
                      fontWeight: 600,
                      color: '#1e293b',
                      marginBottom: 16,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <XCircle size={20} color="#ef4444" style={{ marginRight: 8 }} />
                    Incorrect Answers ({incorrect.length})
                  </h3>

                  {incorrect.map((q, idx) => {
                    const qid = String(q.questionId);
                    const userAns = answers[qid];
                    const correct = Array.isArray(q.correctAnswers)
                      ? q.correctAnswers.map((a) => a.toString().trim().toUpperCase())
                      : (q.correctAnswer
                          ? q.correctAnswer
                              .toString()
                              .split(',')
                              .map((a) => a.trim().toUpperCase())
                          : []);

                    const userLetters = (userAns || [])
                      .map((ans) => {
                        const idx = (q.options || []).findIndex(
                          (o) => o.trim() === ans.trim()
                        );
                        return idx >= 0 ? alphabet[idx] : null;
                      })
                      .filter(Boolean);

                    return (
                      <div key={qid} style={cardStyle}>
                        <p
                          style={{
                            fontWeight: 600,
                            color: '#1e293b',
                            marginBottom: 12,
                            lineHeight: 1.5,
                            whiteSpace: 'pre-line',
                          }}
                        >
                          {idx + 1}. {q.question || '(Question text unavailable)'}
                        </p>

                        {q.code && (
                          <pre
                            style={{
                              backgroundColor: '#f9fafb',
                              borderRadius: 8,
                              padding: '12px',
                              overflowX: 'auto',
                              fontFamily: 'monospace',
                              fontSize: 14,
                            }}
                          >
                            {q.code}
                          </pre>
                        )}

                        <p style={{ fontSize: 14, color: '#374151', marginBottom: 4 }}>
                          <strong>Your answer:</strong>{' '}
                          {userLetters.length > 0
                            ? userLetters.join(', ')
                            : '— Not answered —'}
                        </p>

                        <p style={{ fontSize: 14, color: '#374151', marginBottom: 8 }}>
                          <strong>Expected:</strong>{' '}
                          {correct.length > 0 ? correct.join(', ') : 'Not available'}
                        </p>

                        {q.explanation && (
                          <div
                            style={{
                              backgroundColor: '#f0f9ff',
                              border: '1px solid #bae6fd',
                              borderRadius: 8,
                              padding: 12,
                              fontSize: 14,
                              color: '#0369a1',
                            }}
                          >
                            <strong>Explanation:</strong> {q.explanation}
                          </div>
                        )}

                        <div
                          style={{
                            marginTop: 12,
                            display: 'flex',
                            alignItems: 'center',
                            color: '#ef4444',
                            fontWeight: 600,
                          }}
                        >
                          <XCircle size={18} style={{ marginRight: 6 }} /> Incorrect
                        </div>
                      </div>
                    );
                  })}
                </>
              )}


            </>
          )}

          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <button
              onClick={onBack}
              style={{
                backgroundColor: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '12px 24px',
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 4px 10px rgba(102,126,234,0.3)',
              }}
            >
              Back to Results
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamReview;